
import {
  computed,
  defineComponent,
  onBeforeUpdate,
  onMounted,
  onUnmounted,
  ref,
} from "vue";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";
import EditTemplateCommand from "@/components/EditTemplateCommand.vue";
import EditCommand from "@/components/EditCommand.vue";
import EditArea from "@/components/EditArea.vue";
import EditTitle from "@/components/EditTitle.vue";
import store, { IEditor, EditStore } from "@/stores";
import {
  emptyEditData,
  SelectedTemplate,
  SaveTemplate,
  consts,
  makeTemplteImages,
  emptyEditSet,
  makeSampleFrames,
} from "@/services/Models";
import { useLogger } from "@/lib/logger";
import { routeNames } from "@/router/index";
import { microWait, flatPromise } from "@/lib/shared";
import { useLoginStore } from "@/stores/modules/login";
import { ImageFrame } from "@/components/EditDownload.vue";
import { useDownloader } from "@/lib/downloader";
import { useLoading } from "@/lib/loading";

export default defineComponent({
  name: "EditTemplate",
  components: {
    EditTemplateCommand,
    EditCommand,
    EditArea,
    EditTitle,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const resource = store.useResource();
    const undo = store.useUndoRedo();
    const isShowEditTitle = ref(false);
    const logger = useLogger();
    const login = useLoginStore();
    const loading = useLoading();
    const downloader = useDownloader();
    const editTemplateCommand = ref();

    // 指定のテンプレートを取得
    const templateID = Number.parseInt(route.params.templateID as string);
    const template = computed(() => {
      if (templateID === 0) {
        // テンプレートIDなし(コマ入れ替えあり／セリフ付きサンプル)はITemplateSetを生成する
        return {
          id: undefined,
          frames: [],
          thumbnail: "",
          tags: [],
          describe: "",
          deleteFlg: false,
        };
      } else {
        return resource.data.templateSets.find((x) => x.id == templateID);
      }
    });
    const editStore = ref<EditStore>();

    // セリフ付きサンプルデータの場合はsampleIDを取得（それ以外は0を設定）
    const sampleID = Number.parseInt((route.query.sampleID ?? "0") as string);

    // 画像変換用に編集エリアをBindする
    const editAreas = ref<IEditor[]>([]);
    const setEditArea = (el: IEditor) => {
      editAreas.value.push(el);
    };
    onBeforeUpdate(() => {
      editAreas.value = [];
    });

    //////////////// 自動保存タイマ
    // const autoSaveTimer = ref(0);
    // const autoSaveInterval = Number.parseInt(
    //   process.env.VUE_APP_AUTOSAVE_INTERVAL_SECONDS
    // );
    // const autoSave = async () => {
    //   await props.editStore?.saveData();
    // };
    const beforeEditState = ref("");
    const saveBeforeData = () => {
      if (!editStore.value) return;
      // 遷移時の編集状態を保持
      beforeEditState.value = JSON.stringify(editStore.value.state.editData);
    };
    const isChanged = () => {
      if (editStore.value === undefined) return false;
      // 今の編集状態と遷移時の状態を比較して差異があるか
      const currentState = JSON.stringify(editStore.value.state.editData);
      if (currentState != beforeEditState.value) return true;
      return false;
    };
    onMounted(async () => {
      // autoSaveTimer.value = setInterval(autoSave, autoSaveInterval);

      // 画面遷移前確認
      window.onbeforeunload = function () {
        const isChange = isChanged();
        return isChange ? consts.moveConfirmMessage : null;
      };
      // ログアウト前確認
      login.beforeSignout.value = () => {
        if (isChanged()) {
          return window.confirm(consts.moveConfirmMessage);
        }
        return true;
      };

      //ユーザーログ書き込み(これで制作時のログ)
      const sendLogInfo: SelectedTemplate = {
        action: routeNames.EditTemplate,
        options: {
          templateId: templateID,
        },
      };
      logger.log(sendLogInfo);

      if (template.value === undefined) {
        console.log("template not found");
        router.push({ name: routeNames.SelectTemplate });
        return;
      }

      // 指定の編集データIDを取得
      let editID = -1;
      if (route.query.editID !== undefined) {
        editID = Number.parseInt(route.query.editID as string);
      }

      // 編集データがあれば取得
      const loadEditData =
        (await resource.getEditData(editID, templateID)) ??
        emptyEditData(template.value);

      // セリフ付きサンプル指定の場合はサンプルデータから編集データを生成し使用する
      if (sampleID != 0) {
        const sampleSet = resource.data.sampleSets.find(
          (f) => f.id == sampleID
        );
        if (sampleSet == undefined) return;
        // 以降はコマ入れ替えあり編集データとして振る舞う
        loadEditData.frames = makeSampleFrames(
          JSON.parse(JSON.stringify(sampleSet.frames))
        );
        loadEditData.editset = emptyEditSet();
      }

      // コマ入れ替えを想定して編集データの内容に従いテンプレート画像を再設定する
      template.value.frames = makeTemplteImages(
        loadEditData,
        resource.data.templateSets
      );
      // コマ入れ替えありはサムネイル画像パスを設定する
      if (loadEditData.editset) {
        template.value.thumbnail = loadEditData.editset.thumbnailPath ?? "";
      }

      // 編集サービスを作成
      editStore.value = store.useEditStore(
        // オリジナルを保持するためtemplateはDeepCopyする
        JSON.parse(JSON.stringify(template.value)),
        loadEditData,
        undo,
        resource
      );

      await microWait();

      for (let i = 0; i < editAreas.value.length; i++) {
        editStore.value.setEditor(i, editAreas.value[i]);
      }

      // 遷移時の編集状態を保持
      saveBeforeData();

      // 上限に達してたら警告を出す
      if (editStore.value.state.editData.id === undefined) {
        if (consts.maxEditDataCount <= resource.data.editDatas.length) {
          alert(consts.maxEditDataCountOver);
        }
      }
    });
    onUnmounted(() => {
      window.onbeforeunload = null;
      login.beforeSignout.value = undefined;
      // clearInterval(autoSaveTimer.value);
    });

    onBeforeRouteLeave((to, from, next) => {
      if (!isChanged()) {
        next();
        return;
      }
      const answer = window.confirm(consts.moveConfirmMessage);
      if (answer) {
        next();
      } else {
        next(false);
      }
    });

    const setTitle = (title: string) => {
      editStore.value?.setTitle(title);
      isShowEditTitle.value = false;
    };
    const save = async (title: string, images: ImageFrame[]) => {
      loading.loadWith(async () => {
        if (!editStore.value) return;
        const sendLogInfo: SaveTemplate = {
          action: routeNames.EditTemplate,
          options: {
            templateId: editStore.value.state.template.id ?? 0,
          },
        };
        //ユーザーログ書き込み(これで制作時のログ)
        await logger.log(sendLogInfo);

        // サムネイル画像生成(コマ入れ替え編集データのみ)
        if (
          images.length != 0 &&
          editStore.value.state.editData.editset != undefined
        ) {
          const editedImgs = await flatPromise(images.map((x) => x.edited()));
          // サムネイルデータを生成
          const dataURI = await downloader.toImageThumbnail(await editedImgs);
          // サムネイルデータを編集データに設定
          editStore.value.state.editData.editset.thumbnailImage = dataURI;
        }

        // 保存
        const savedData = await editStore.value.saveData(title);

        // 更新判定用の編集状態を更新
        if (savedData) {
          saveBeforeData();
        }
        // 保存ダイアログを閉じる
        editTemplateCommand.value.isShowSave = false;
      });
    };

    return {
      setEditArea,
      isShowEditTitle,
      editStore,
      setTitle,
      save,
      undo,
      editTemplateCommand,
    };
  },
});
